import colors from 'tailwindcss/colors';

import { OrderStatus } from '../chat/utils/order-status';
import { IField } from '../forms/interfaces/IField';
import {
	LiveHagglesIcon,
	NotificationsIcon,
	ProfileIcon,
} from '../ui/svg/NavIcons';
import { SubscriptionPlans } from './enums';

import { PlanItem } from '@/interfaces/api/configApp/IPlanItem';
import { FilterList } from '@/interfaces/haggle/FilterList.enum';

export const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const BRAND_COLORS = {
	PURPLE_500: '#9867C5',
	PURPLE_600: '#8A5BB8',
	PURPLE_900: '#7A4BA8',
} as const;

export const ConsumerRouteList = [
	{
		name: 'Haggle Hub',
		path: '/',
		showInMenu: true,
		icon: LiveHagglesIcon,
	},
	{
		name: 'Profile',
		path: '/customer/profile',
		showInMenu: true,
		icon: ProfileIcon,
	},
	{
		name: 'Add Profile',
		path: '/customer/profile/add',
		showInMenu: false,
		icon: ProfileIcon,
	},
	{
		name: 'Activities',
		path: '/customer/notifications',
		showInMenu: true,
		icon: NotificationsIcon,
	},
	{
		name: 'Orders',
		path: '/customer/orders',
		showInMenu: true,
		icon: NotificationsIcon,
	},
];

export const productInterestOptions = [
	{ value: 'electronics', label: 'Electronics' },
	{ value: 'fashion', label: 'Fashion' },
	{ value: 'home', label: 'Home' },
	{ value: 'beauty', label: 'Beauty' },
	{ value: 'sports', label: 'Sports' },
	{ value: 'toys', label: 'Toys' },
	{ value: 'books', label: 'Books' },
	{ value: 'food', label: 'Food' },
	{ value: 'other', label: 'Other' },
];

export const MAX_LIMIT = 100000;

export function generateUpgradeMessage({
	currentPlan,
	nextPlan,
	isNearLimit = false,
}: {
	currentPlan: PlanItem;
	nextPlan?: PlanItem; // Next plan can be undefined if there's no upgrade
	isNearLimit?: boolean;
}): string {
	const prefix = isNearLimit
		? `You have reached the 90% limit of ${currentPlan.limit} haggles`
		: `You have reached the limit of ${currentPlan.limit} haggles`;

	if (!nextPlan) {
		return `${prefix}. Please contact support for custom upgrade options.`;
	}

	const suffix =
		nextPlan.limit === MAX_LIMIT
			? `Please upgrade to ${nextPlan.planName} plan for up to ${nextPlan.limit} haggles per month.`
			: `Please upgrade to ${nextPlan.planName} plan for unlimited haggles.`;

	return `${prefix}. ${suffix}`;
}

export const STRIPE_STATUS_MESSAGES = {
	create: `You need to connect your payment account to receive payouts. This step is crucial if you want to host haggles here. Your customers won't be able to checkout unless payment connect set-up is complete. You can either select your country and add a bank account or link an existing account.`,
	incomplete: `Some requirements are pending which is why your stripe account is restricted. Click this link to complete your onboarding.`,
	complete: `Bank details successfully connected for local checkouts ✅`,
	disabled: `Your Stripe account payouts are disabled. Please confirm and review your account.`,
	pending: `Your Stripe account is pending verification. You will be able to receive payouts once the verification is complete.`,
};

export const ChatbotThemeColors = [
	{ name: 'color1', value: colors.red[600] },
	{ name: 'color2', value: colors.yellow[400] },
	{ name: 'color3', value: colors.green[300] },
	{ name: 'color4', value: colors.purple[600] },
	{ name: 'color5', value: colors.green[500] },
	{ name: 'color6', value: colors.cyan[400] },
] as const;

export const ChatbotThemeGradients = [
	{
		name: 'gradient1',
		value: 'linear-gradient(90deg, #695ECC, #52ABCB)',
	}, // light peach
	{
		name: 'gradient2',
		value: 'linear-gradient(135deg, #fbc2eb 0%, #a6c1ee 100%)',
	}, // pink-purple
	{
		name: 'gradient3',
		value: 'linear-gradient(135deg, #d4fc79 0%, #96e6a1 100%)',
	}, // mint green
	{
		name: 'gradient4',
		value: 'linear-gradient(135deg, #89f7fe 0%, #66a6ff 100%)',
	}, // sky blue
	{
		name: 'gradient5',
		value: 'linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)',
	}, // blue blend
	{
		name: 'gradient6',
		value: 'linear-gradient(135deg, #f953c6 0%, #b91d73 100%)',
	}, // magenta-purple
] as const;

export const defaultAvatars = [
	'https://bucket-api-haggle-ap-southeast-1-dev.s3.ap-southeast-1.amazonaws.com/Defaults/5d428528-bc4f-4cab-b0b3-e20138b0ed43.svg',
	'https://bucket-api-haggle-ap-southeast-1-dev.s3.ap-southeast-1.amazonaws.com/Defaults/06e8f357-817e-4106-9798-d06df4d98831.svg',
	'https://bucket-api-haggle-ap-southeast-1-dev.s3.ap-southeast-1.amazonaws.com/Defaults/93131768-8711-4a68-840e-429d35d1f8d5.svg',
	'https://bucket-api-haggle-ap-southeast-1-dev.s3.ap-southeast-1.amazonaws.com/Defaults/285437d3-f7d1-4c1c-b806-dc211eed378b.svg',
	'https://bucket-api-haggle-ap-southeast-1-dev.s3.ap-southeast-1.amazonaws.com/Defaults/8083abd2-4606-4653-b406-254d231dcddb.svg',
	'https://bucket-api-haggle-ap-southeast-1-dev.s3.ap-southeast-1.amazonaws.com/Defaults/09ca6532-29b3-4731-afb5-6afe1fa8fc99.svg',
	'https://bucket-api-haggle-ap-southeast-1-dev.s3.ap-southeast-1.amazonaws.com/Defaults/ef93d755-7a52-4a0b-a775-0e6f897e03b6.svg',
	'https://bucket-api-haggle-ap-southeast-1-dev.s3.ap-southeast-1.amazonaws.com/Defaults/ba4fb9f5-6fcf-47e6-9905-6d64d782b950.svg',
	'https://bucket-api-haggle-ap-southeast-1-dev.s3.ap-southeast-1.amazonaws.com/Defaults/3e1de106-fde8-4311-b575-ec1161447c04.svg',
	'https://bucket-api-haggle-ap-southeast-1-dev.s3.ap-southeast-1.amazonaws.com/Defaults/9ce4fd81-f18b-4e07-90e1-3f1e621454b6.svg',
];

export const installationInstructions = [
	{
		title: 'Click "Install Haggle App"',
		description: "You'll be redirected to the Shopify App Store.",
	},
	{
		title: 'Install & Authorize',
		description: 'On Shopify, click "Install App" to authorize the connection.',
	},
	{
		title: 'Access Your Dashboard',
		description:
			'After installation, you will be taken to Haggle App Dashboard.',
	},
	{
		title: 'Connect Your Store',
		description:
			'Click "Connect Store" to link Haggle with your Shopify store.',
	},
	{
		title: 'Activate the Chatbot',
		description: 'Click "Install Chatbot" to enable it on your store.',
	},
];

export const haggleEventFilterList = [
	{ label: FilterList.ACTIVE },
	{ label: FilterList.FINISHED },
	{ label: FilterList.PENDING },
	{ label: FilterList.MIN_MARGIN },
	{ label: FilterList.MAX_MARGIN },
	{ label: FilterList.NEWEST },
	{ label: FilterList.OLDEST },
];

export const contactFormFields: IField[] = [
	{
		name: 'name',
		label: 'Name *',
		type: 'text',
		placeholder: 'Your name',
	},
	{
		name: 'email',
		label: 'Email *',
		type: 'email',
		placeholder: 'your@email.com',
	},
	{
		name: 'subject',
		label: 'Subject *',
		type: 'text',
		placeholder: 'Message subject',
	},
	{
		name: 'message',
		label: 'Message *',
		type: 'textarea',
		placeholder: 'Your message...',
	},
];

export const orderFilterList = [
	{ label: OrderStatus.CANCELLED },
	{ label: OrderStatus.DELIVERED },
	{ label: OrderStatus.PENDING },
	{ label: OrderStatus.PROCESSING },
	{ label: OrderStatus.SHIPPED },
];
const subscriptionPlansDetails = [
	{
		background: '/diamondButton.png',
		badgeColor: 'bg-diamondBrand-500',
		borderColor: 'border-diamondBrand-500',
		shadowColor: 'shadow-[0_0_10px_#60E6F9] border-2 border-diamondBrand-500',
		planName: SubscriptionPlans.diamond,
	},
	{
		background: '/rubyButton.jpg',
		badgeColor: 'bg-redBrand-300',
		borderColor: 'border-redBrand-300',
		shadowColor: 'shadow-[0_0_10px_#E9869F] border-2 border-redBrand-300',
		planName: SubscriptionPlans.ruby,
	},
	{
		background: '/sapphireButton.png',
		badgeColor: 'bg-blue-400',
		borderColor: 'border-blue-400',
		shadowColor: 'shadow-[0_0_10px_#60A5FA] border-2 border-blue-400',
		planName: SubscriptionPlans.sapphire,
	},
	{
		background: '/emeraldButton.png',
		badgeColor: 'bg-greenBrand-300',
		borderColor: 'border-greenBrand-300',
		shadowColor: 'shadow-[0_0_10px_#4EA75E] border-2 border-greenBrand-300',
		plan: SubscriptionPlans.emerald,
	},
];

export function getPlanStyle(planName: string) {
	return (
		subscriptionPlansDetails.find((plan) => plan.planName === planName) ??
		subscriptionPlansDetails[0]
	);
}

export const DEFAULT_PAGE_SIZE = 30;
export const PAGE_SIZE_OPTIONS = [30, 40, 50];

// Product Selection Limits
export const MAX_PRODUCTS_SELECTION = 100;

// How-to Videos Constants
export const HOW_TO_VIDEOS_CSV = [
	{
		title: 'Onboarding products using CSV on Haggles hub',
		url: 'https://drive.google.com/file/d/1L3rFImTVV0zVrdj4J4aNLpMHurcq96NM/preview',
		description:
			'Learn how to efficiently onboard your products using CSV files on the Haggles hub platform.',
	},
	{
		title: 'Haggle Event creation (Live and Upcoming)',
		url: 'https://drive.google.com/file/d/1w0PMF9HyJwCCLQ6JLPqFm6NigWis6hwv/preview',
		description:
			'Complete guide on creating haggle events, including both live and upcoming haggle configurations.',
	},
	{
		title: 'Create CSV Inventory for bulk product upload',
		url: 'https://drive.google.com/file/d/1G7lTZSr9lJBSUQdtKuQQad_icgtNSLdc/preview',
		description:
			'Step-by-step tutorial on creating CSV inventory files for efficient bulk product uploads.',
	},
] as const;

export const HOW_TO_VIDEOS_SHOPIFY = [
	{
		title: 'Haggle Event creation (Live and Upcoming)',
		url: 'https://drive.google.com/file/d/1w0PMF9HyJwCCLQ6JLPqFm6NigWis6hwv/preview',
		description:
			'Complete guide on creating haggle events, including both live and upcoming haggle configurations.',
	},
] as const;

// CSV Template Constants
export const CSV_TEMPLATE_PATH = '/sample_inventory_template.csv';
export const MAX_IMAGE_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes
